import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  adminFetchBtwStatuses,
  adminCreateBtwStatus,
  adminUpdateBtwStatus,
  adminArchiveBtwStatus,
  adminReorderBtwStatuses,
} from "../../core/axios/endpoints";
import { COLOR_OPTIONS, getStatusColor } from "../../core/btw-status-colors";
import ConfirmModal from "../../components/confirm-modal";

const ColorSwatch = ({ color, selected, onClick }) => {
  const c = getStatusColor(color);
  return (
    <button
      type="button"
      onClick={onClick}
      title={color}
      className={`flex h-7 w-7 items-center justify-center rounded-full transition ${
        selected ? "ring-2 ring-gray-900 ring-offset-2" : "hover:scale-110"
      }`}
    >
      <span className={`h-4 w-4 rounded-full ${c.dot}`} />
    </button>
  );
};

const StatusRow = ({ status, isEditing, onEdit, onCancel, onSave, onArchive, busy }) => {
  const [draft, setDraft] = useState({
    name: status.name,
    color: status.color,
    isTerminal: status.isTerminal,
    isDefault: status.isDefault,
  });

  useEffect(() => {
    setDraft({
      name: status.name,
      color: status.color,
      isTerminal: status.isTerminal,
      isDefault: status.isDefault,
    });
  }, [status.name, status.color, status.isTerminal, status.isDefault, isEditing]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status._id, disabled: isEditing });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const c = getStatusColor(status.color);
  const draftC = getStatusColor(draft.color);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-xl bg-white p-4 shadow-sm ring-1 transition ${
        isEditing ? "ring-emerald-300" : "ring-gray-200"
      }`}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className={`flex h-8 w-6 cursor-grab items-center justify-center text-gray-400 hover:text-gray-700 active:cursor-grabbing ${
            isEditing ? "cursor-not-allowed opacity-30" : ""
          }`}
          aria-label="Versleep"
          disabled={isEditing}
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
            <circle cx="7" cy="5" r="1.5" />
            <circle cx="13" cy="5" r="1.5" />
            <circle cx="7" cy="10" r="1.5" />
            <circle cx="13" cy="10" r="1.5" />
            <circle cx="7" cy="15" r="1.5" />
            <circle cx="13" cy="15" r="1.5" />
          </svg>
        </button>

        <span className={`h-3 w-3 flex-shrink-0 rounded-full ${c.dot}`} />

        <div className="min-w-0 flex-1">
          {isEditing ? (
            <input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              autoFocus
              className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm font-medium text-gray-900 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
            />
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900">{status.name}</span>
              {status.isDefault ? (
                <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                  Standaard
                </span>
              ) : null}
              {status.isTerminal ? (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                  Eindstation
                </span>
              ) : null}
              {status.archived ? (
                <span className="inline-flex items-center rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-medium text-rose-700">
                  Gearchiveerd
                </span>
              ) : null}
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={() => onSave(draft)}
              disabled={busy || !draft.name.trim()}
              className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
            >
              {busy ? "Opslaan…" : "Opslaan"}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onEdit}
              className="rounded-md px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            >
              Bewerken
            </button>
            <button
              type="button"
              onClick={onArchive}
              className={`rounded-md px-2.5 py-1.5 text-sm hover:bg-gray-100 ${
                status.archived ? "text-emerald-700" : "text-gray-500"
              }`}
            >
              {status.archived ? "Herstellen" : "Archiveren"}
            </button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="mt-4 grid gap-4 border-t border-gray-100 pt-4 sm:grid-cols-2">
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              Kleur
            </div>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((co) => (
                <ColorSwatch
                  key={co}
                  color={co}
                  selected={draft.color === co}
                  onClick={() => setDraft({ ...draft, color: co })}
                />
              ))}
            </div>
            <div className="mt-3">
              <span className="text-xs text-gray-500">Voorbeeld: </span>
              <span
                className={`ml-1 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${draftC.pill}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${draftC.dot}`} />
                {draft.name || status.name}
              </span>
            </div>
          </div>
          <div className="space-y-3">
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                checked={draft.isTerminal}
                onChange={(e) => setDraft({ ...draft, isTerminal: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-400"
              />
              <span className="text-sm">
                <span className="font-medium text-gray-900">Eindstation</span>
                <span className="block text-xs text-gray-500">
                  Aanvragen in deze status worden niet meegerekend als &lsquo;vastgelopen&rsquo;.
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                checked={draft.isDefault}
                onChange={(e) => setDraft({ ...draft, isDefault: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-400"
              />
              <span className="text-sm">
                <span className="font-medium text-gray-900">Standaard-status</span>
                <span className="block text-xs text-gray-500">
                  Nieuwe aanvragen starten in deze status. Slechts één status kan standaard zijn.
                </span>
              </span>
            </label>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const NewStatusForm = ({ onCancel, onSave, busy }) => {
  const [draft, setDraft] = useState({
    name: "",
    color: "slate",
    isTerminal: false,
    isDefault: false,
  });
  const c = getStatusColor(draft.color);
  return (
    <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-emerald-300">
      <div className="mb-3 flex items-center gap-2">
        <span className={`h-3 w-3 rounded-full ${c.dot}`} />
        <input
          type="text"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="Naam van de nieuwe status"
          autoFocus
          className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Kleur
          </div>
          <div className="flex flex-wrap gap-2">
            {COLOR_OPTIONS.map((co) => (
              <ColorSwatch
                key={co}
                color={co}
                selected={draft.color === co}
                onClick={() => setDraft({ ...draft, color: co })}
              />
            ))}
          </div>
        </div>
        <div className="space-y-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.isTerminal}
              onChange={(e) => setDraft({ ...draft, isTerminal: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-400"
            />
            Eindstation
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.isDefault}
              onChange={(e) => setDraft({ ...draft, isDefault: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-400"
            />
            Standaard-status
          </label>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Annuleren
        </button>
        <button
          type="button"
          onClick={() => onSave(draft)}
          disabled={busy || !draft.name.trim()}
          className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
        >
          {busy ? "Aanmaken…" : "Status aanmaken"}
        </button>
      </div>
    </div>
  );
};

const BtwBatterijStatussenPage = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [archivePrompt, setArchivePrompt] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = (withArchived) => {
    setLoading(true);
    return adminFetchBtwStatuses({ includeArchived: withArchived ?? showArchived })
      .then((res) => setStatuses(res?.data ?? res ?? []))
      .catch((err) =>
        setError(err?.response?.data?.error ?? err.message ?? "Onbekende fout"),
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(showArchived);
  }, [showArchived]);

  const activeIds = useMemo(
    () => statuses.filter((s) => !s.archived).map((s) => s._id),
    [statuses],
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = activeIds.indexOf(active.id);
    const newIdx = activeIds.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(activeIds, oldIdx, newIdx);
    const items = reordered.map((id, order) => ({ id, order }));

    setStatuses((prev) => {
      const map = Object.fromEntries(prev.map((s) => [s._id, s]));
      const archivedTail = prev.filter((s) => s.archived);
      return [...reordered.map((id) => ({ ...map[id], order: items.find((it) => it.id === id).order })), ...archivedTail];
    });

    try {
      await adminReorderBtwStatuses(items);
    } catch (err) {
      console.error(err);
      await load();
    }
  };

  const handleSave = async (id, draft) => {
    setBusy(true);
    try {
      await adminUpdateBtwStatus(id, draft);
      await load();
      setEditingId(null);
    } catch (err) {
      window.alert(err?.response?.data?.error ?? "Bijwerken mislukt.");
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async (draft) => {
    setBusy(true);
    try {
      await adminCreateBtwStatus(draft);
      await load();
      setCreating(false);
    } catch (err) {
      window.alert(err?.response?.data?.error ?? "Aanmaken mislukt.");
    } finally {
      setBusy(false);
    }
  };

  const handleArchive = async () => {
    if (!archivePrompt) return;
    setBusy(true);
    try {
      await adminArchiveBtwStatus(archivePrompt._id, {
        archive: !archivePrompt.archived,
      });
      await load();
      setArchivePrompt(null);
    } catch (err) {
      window.alert(err?.response?.data?.error ?? "Archiveren mislukt.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-wrapper px-6 pt-24 pb-12">
      <div className="mb-4">
        <Link
          to="/panel/admin/btw-batterij-board"
          className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M12.7 15.7a1 1 0 01-1.4 0l-5-5a1 1 0 010-1.4l5-5a1 1 0 111.4 1.4L8.4 10l4.3 4.3a1 1 0 010 1.4z"
              clipRule="evenodd"
            />
          </svg>
          Terug naar BTW Batterij
        </Link>
      </div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-900">
            BTW workflow-statussen
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Beheer de statussen die aanvragen kunnen doorlopen. Sleep om de volgorde
            op het board te wijzigen. Gearchiveerde statussen blijven gekoppeld aan
            bestaande aanvragen maar verschijnen niet meer in keuze-menu&apos;s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-400"
            />
            Toon gearchiveerd
          </label>
          <button
            type="button"
            onClick={() => setCreating(true)}
            disabled={creating}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Nieuwe status
          </button>
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <div className="space-y-3">
        {creating ? (
          <NewStatusForm
            busy={busy}
            onCancel={() => setCreating(false)}
            onSave={handleCreate}
          />
        ) : null}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={activeIds} strategy={verticalListSortingStrategy}>
            {statuses
              .filter((s) => !s.archived)
              .map((s) => (
                <StatusRow
                  key={s._id}
                  status={s}
                  isEditing={editingId === s._id}
                  busy={busy}
                  onEdit={() => setEditingId(s._id)}
                  onCancel={() => setEditingId(null)}
                  onSave={(draft) => handleSave(s._id, draft)}
                  onArchive={() => setArchivePrompt(s)}
                />
              ))}
          </SortableContext>
        </DndContext>

        {showArchived && statuses.some((s) => s.archived) ? (
          <div className="pt-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              Gearchiveerd
            </div>
            <div className="space-y-3 opacity-80">
              {statuses
                .filter((s) => s.archived)
                .map((s) => {
                  const c = getStatusColor(s.color);
                  return (
                    <div
                      key={s._id}
                      className="flex items-center gap-3 rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200"
                    >
                      <span className={`h-3 w-3 rounded-full ${c.dot}`} />
                      <span className="flex-1 text-sm text-gray-700">{s.name}</span>
                      <button
                        type="button"
                        onClick={() => setArchivePrompt(s)}
                        className="rounded-md px-2.5 py-1.5 text-sm text-emerald-700 hover:bg-gray-100"
                      >
                        Herstellen
                      </button>
                    </div>
                  );
                })}
            </div>
          </div>
        ) : null}

        {!loading && statuses.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 px-4 py-12 text-center text-sm text-gray-500">
            Geen statussen geconfigureerd.
          </div>
        ) : null}
      </div>

      <ConfirmModal
        isOpen={!!archivePrompt}
        title={
          archivePrompt?.archived
            ? "Status herstellen?"
            : "Status archiveren?"
        }
        message={
          archivePrompt?.archived
            ? "Deze status wordt weer beschikbaar in keuze-menu's."
            : "Aanvragen op deze status moeten worden verplaatst voordat je 'm kunt archiveren. De server zal je waarschuwen als er nog aanvragen gekoppeld zijn."
        }
        confirmLabel={archivePrompt?.archived ? "Herstellen" : "Archiveren"}
        cancelLabel="Annuleren"
        busy={busy}
        onConfirm={handleArchive}
        onCancel={() => setArchivePrompt(null)}
      />

    </div>
  );
};

export default BtwBatterijStatussenPage;
